import { useState, useEffect, useCallback } from 'react';
import {
  adminGetEmpresas, adminCriarEmpresa, adminEditarEmpresa,
  adminDeletarEmpresa, adminToggleEmpresa,
} from '../services/api';
import { PageHeader, SearchBar, Table, Loading, Erro, Badge } from '../components/UI';
import './Empresas.css';

const VAZIO = { nome: '', api_url: '', empresa_id: '', api_key: '' };

export default function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [busca,    setBusca]    = useState('');
  const [load,     setLoad]     = useState(true);
  const [erro,     setErro]     = useState('');

  // Modal form
  const [modal,    setModal]    = useState(false);
  const [editando, setEditando] = useState(null); // null = novo, objeto = editar
  const [form,     setForm]     = useState(VAZIO);
  const [formErro, setFormErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Modal confirmação delete
  const [confirmDel, setConfirmDel] = useState(null);
  const [deletando,  setDeletando]  = useState(false);

  const carregar = useCallback(async () => {
    setLoad(true); setErro('');
    try { setEmpresas(await adminGetEmpresas()); }
    catch (e) { setErro(e.message); }
    finally { setLoad(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = empresas.filter(e =>
    e.nome.toLowerCase().includes(busca.toLowerCase()) ||
    e.empresa_id.toLowerCase().includes(busca.toLowerCase())
  );

  // ─── Abrir modal ────────────────────────────────────────────────────────────

  const abrirNovo = () => {
    setEditando(null);
    setForm(VAZIO);
    setFormErro('');
    setModal(true);
  };

  const abrirEditar = (emp) => {
    setEditando(emp);
    setForm({ nome: emp.nome, api_url: emp.api_url, empresa_id: emp.empresa_id, api_key: emp.api_key });
    setFormErro('');
    setModal(true);
  };

  const fecharModal = () => { if (!salvando) { setModal(false); setEditando(null); } };

  // ─── Salvar ─────────────────────────────────────────────────────────────────

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.api_url.trim() || !form.empresa_id.trim() || !form.api_key.trim()) {
      setFormErro('Preencha todos os campos obrigatórios.'); return;
    }
    setSalvando(true); setFormErro('');
    try {
      if (editando) {
        await adminEditarEmpresa(editando.id, form);
      } else {
        await adminCriarEmpresa(form);
      }
      setModal(false);
      await carregar();
    } catch (err) {
      setFormErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  // ─── Toggle ─────────────────────────────────────────────────────────────────

  const toggle = async (emp) => {
    try {
      const res = await adminToggleEmpresa(emp.id);
      setEmpresas(prev => prev.map(e => e.id === emp.id ? { ...e, ativo: res.ativo } : e));
    } catch (err) { alert(err.message); }
  };

  // ─── Deletar ────────────────────────────────────────────────────────────────

  const confirmarDelete = (emp) => setConfirmDel(emp);

  const deletar = async () => {
    if (!confirmDel) return;
    setDeletando(true);
    try {
      await adminDeletarEmpresa(confirmDel.id);
      setConfirmDel(null);
      await carregar();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletando(false);
    }
  };

  // ─── Colunas ────────────────────────────────────────────────────────────────

  const colunas = [
    { key: 'id',         label: 'ID',        width: '60px' },
    { key: 'nome',       label: 'Nome',      width: '220px' },
    { key: 'empresa_id', label: 'Empresa ID' },
    { key: 'api_url',    label: 'API URL',   render: v => <span className="emp-url">{v}</span> },
    {
      key: 'ativo', label: 'Status', width: '100px',
      render: (v) => <Badge valor={v ? 'Ativo' : 'Inativo'} tipo={v ? 'success' : 'danger'} />,
    },
    {
      key: '_acoes', label: 'Ações', width: '140px',
      render: (_, row) => (
        <div className="emp-acoes">
          <button className="emp-btn emp-btn--edit" onClick={e => { e.stopPropagation(); abrirEditar(row); }} title="Editar">
            <IcoEdit />
          </button>
          <button
            className={`emp-btn emp-btn--toggle ${row.ativo ? 'emp-btn--off' : 'emp-btn--on'}`}
            onClick={e => { e.stopPropagation(); toggle(row); }}
            title={row.ativo ? 'Desativar' : 'Ativar'}
          >
            {row.ativo ? <IcoOff /> : <IcoOn />}
          </button>
          <button className="emp-btn emp-btn--del" onClick={e => { e.stopPropagation(); confirmarDelete(row); }} title="Excluir">
            <IcoTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader titulo="Empresas" subtitulo="Gerencie as empresas e suas conexões com o ERP">
        <button className="emp-novo-btn" onClick={abrirNovo}>+ Nova Empresa</button>
      </PageHeader>

      <div className="emp-toolbar">
        <SearchBar value={busca} onChange={setBusca} placeholder="Buscar por nome ou ID..." />
        <span className="emp-total">{filtradas.length} empresa{filtradas.length !== 1 ? 's' : ''}</span>
      </div>

      {erro && <Erro mensagem={erro} onRetry={carregar} />}
      {load ? <Loading /> : (
        <Table
          colunas={colunas}
          dados={filtradas}
          vazio="Nenhuma empresa cadastrada."
        />
      )}

      {/* ─── Modal Form ─────────────────────────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Empresa' : 'Nova Empresa'}</h2>
              <button className="modal-close" onClick={fecharModal}>✕</button>
            </div>
            <form onSubmit={salvar} className="modal-form">
              <div className="mf-group">
                <label>Nome *</label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Minha Empresa Ltda" required />
              </div>
              <div className="mf-group">
                <label>URL da API *</label>
                <input value={form.api_url} onChange={e => setForm(f => ({ ...f, api_url: e.target.value }))} placeholder="http://192.168.1.10:8000" required />
              </div>
              <div className="mf-row">
                <div className="mf-group">
                  <label>Empresa ID *</label>
                  <input value={form.empresa_id} onChange={e => setForm(f => ({ ...f, empresa_id: e.target.value }))} placeholder="1" required />
                </div>
                <div className="mf-group">
                  <label>API Key *</label>
                  <input value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} placeholder="chave secreta" required />
                </div>
              </div>
              {formErro && <div className="mf-erro">⚠ {formErro}</div>}
              <div className="modal-footer">
                <button type="button" className="mf-btn mf-btn--cancel" onClick={fecharModal} disabled={salvando}>Cancelar</button>
                <button type="submit" className="mf-btn mf-btn--save" disabled={salvando}>
                  {salvando ? 'Salvando...' : (editando ? 'Salvar alterações' : 'Cadastrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Confirmar Delete ──────────────────────────────────────── */}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => !deletando && setConfirmDel(null)}>
          <div className="modal-box modal-box--sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar exclusão</h2>
              <button className="modal-close" onClick={() => setConfirmDel(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja excluir <strong>{confirmDel.nome}</strong>?</p>
              <p className="modal-warn">Esta ação não pode ser desfeita e pode afetar usuários vinculados.</p>
            </div>
            <div className="modal-footer">
              <button className="mf-btn mf-btn--cancel" onClick={() => setConfirmDel(null)} disabled={deletando}>Cancelar</button>
              <button className="mf-btn mf-btn--danger" onClick={deletar} disabled={deletando}>
                {deletando ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IcoEdit()  { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function IcoTrash() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>; }
function IcoOff()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>; }
function IcoOn()    { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>; }
